import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { lazy, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { AcceptInvitation } from './AcceptInvitation'
import { Overview } from './Overview'
import { AdminFrame } from './AdminFrame'
import { LazyScreens } from './ChunkErrorBoundary'
import { DirectoryArea, MovedIntoDirectory } from './DirectoryArea'
import { Shell } from './Shell'
import { ForgotPassword } from './ForgotPassword'
import { ResetPassword } from './ResetPassword'
import { SignIn } from './SignIn'
import { DeviceSignIn } from './DeviceSignIn'
import { Projects } from './Projects'
import { RequireSession } from './RequireSession'
import { SessionProvider } from './session'
import { UnknownAddress } from './UnknownAddress'
import { UnsettledWorkProvider } from './unsettled-work'

/**
 * The whole application's routing, in one place.
 *
 * The project is in the path — `/p/<project id>/…` — so a link pasted into a
 * chat opens the same project for whoever clicks it, and switching projects is
 * a navigation rather than hidden state. Identity is not in the path and never
 * will be.
 *
 * Three routes sit outside the project entirely: `/signin`; `/signin/reset`,
 * where somebody who cannot sign in at all uses the one-time code an
 * administrator issued them; and `/device`, the trailer tablet's keypad, which
 * has no identity yet and is what produces one.
 *
 * Module screens are loaded on demand. The first paint on a trailer laptop
 * over a site uplink should be the shell and the overview, not eleven modules'
 * worth of code somebody may never open.
 */

const Welcome = lazy(() =>
  import('../welcome/Welcome').then((m) => ({ default: m.Welcome })),
)
const WallScreen = lazy(() =>
  import('../engagement/WallScreen').then((m) => ({ default: m.WallScreen })),
)
const FeedbackMine = lazy(() =>
  import('../feedback/Lists').then((m) => ({ default: m.FeedbackMine })),
)
const FeedbackBoard = lazy(() =>
  import('../feedback/Lists').then((m) => ({ default: m.FeedbackBoard })),
)
const FeedbackReportScreen = lazy(() =>
  import('../feedback/Report').then((m) => ({ default: m.FeedbackReportScreen })),
)
const DeficiencyList = lazy(() =>
  import('../features/deficiency/List').then((m) => ({ default: m.DeficiencyList })),
)
const DeficiencyNew = lazy(() =>
  import('../features/deficiency/New').then((m) => ({ default: m.DeficiencyNew })),
)
const DeficiencyDetail = lazy(() =>
  import('../features/deficiency/Detail').then((m) => ({ default: m.DeficiencyDetail })),
)
const AskForAccessScreen = lazy(() =>
  import('../features/directory/AskForAccess').then((m) => ({ default: m.AskForAccessScreen })),
)
const TemplatesScreen = lazy(() =>
  import('../features/directory/Templates').then((m) => ({ default: m.TemplatesScreen })),
)
const DirectoryScreen = lazy(() =>
  import('../features/directory/Directory').then((m) => ({ default: m.DirectoryScreen })),
)
const AdministrationScreen = lazy(() =>
  import('../features/directory/Administration').then((m) => ({
    default: m.AdministrationScreen,
  })),
)
const PointsScreen = lazy(() =>
  import('../engagement/PointsScreen').then((m) => ({ default: m.PointsScreen })),
)
const WallScreensScreen = lazy(() =>
  import('../engagement/WallScreens').then((m) => ({ default: m.WallScreensScreen })),
)
const IntegrationsScreen = lazy(() =>
  import('../features/directory/Integrations').then((m) => ({ default: m.IntegrationsScreen })),
)
const SignInPolicyScreen = lazy(() =>
  import('../features/directory/SignInPolicy').then((m) => ({ default: m.SignInPolicyScreen })),
)
const ProjectSetupScreen = lazy(() =>
  import('../features/directory/ProjectSetup').then((m) => ({ default: m.ProjectSetupScreen })),
)
const DailyLogList = lazy(() =>
  import('../features/dailylog/List').then((m) => ({ default: m.DailyLogList })),
)
const ScheduleScreen = lazy(() =>
  import('../features/schedule/Schedule').then((m) => ({ default: m.ScheduleScreen })),
)
const LookaheadScreen = lazy(() =>
  import('../features/schedule/Lookahead').then((m) => ({ default: m.LookaheadScreen })),
)
const ScheduleActivity = lazy(() =>
  import('../features/schedule/Activity').then((m) => ({ default: m.ScheduleActivity })),
)
const DailyLogDay = lazy(() =>
  import('../features/dailylog/Day').then((m) => ({ default: m.DailyLogDay })),
)
const FieldReportList = lazy(() =>
  import('../features/fieldreport/List').then((m) => ({ default: m.FieldReportList })),
)
const FieldReportScreen = lazy(() =>
  import('../features/fieldreport/Report').then((m) => ({ default: m.FieldReportScreen })),
)
const PhotosScreen = lazy(() =>
  import('../features/photos/Photos').then((m) => ({ default: m.PhotosScreen })),
)
const SiteComputersScreen = lazy(() =>
  import('../features/directory/SiteComputers').then((m) => ({ default: m.SiteComputersScreen })),
)
const BoardDisplay = lazy(() =>
  import('../engagement/BoardDisplay').then((m) => ({ default: m.BoardDisplay })),
)
const SystemStatusScreen = lazy(() =>
  import('../features/system/SystemStatus').then((m) => ({ default: m.SystemStatusScreen })),
)
const ChangeRegister = lazy(() =>
  import('../features/changes/Register').then((m) => ({ default: m.ChangeRegister })),
)
const ChangeEventNew = lazy(() =>
  import('../features/changes/New').then((m) => ({ default: m.ChangeEventNew })),
)
const ChangeSettings = lazy(() =>
  import('../features/changes/Settings').then((m) => ({ default: m.ChangeSettings })),
)
const ChangeEventDetail = lazy(() =>
  import('../features/changes/Detail').then((m) => ({ default: m.ChangeEventDetail })),
)
const RfiList = lazy(() => import('../features/rfi/List').then((m) => ({ default: m.RfiList })))
const RfiNew = lazy(() => import('../features/rfi/New').then((m) => ({ default: m.RfiNew })))
const RfiRoutes = lazy(() =>
  import('../features/rfi/Routes').then((m) => ({ default: m.RfiRoutes })),
)
const RfiDetail = lazy(() =>
  import('../features/rfi/Detail').then((m) => ({ default: m.RfiDetail })),
)
const CorrespondenceList = lazy(() =>
  import('../features/correspondence/List').then((m) => ({ default: m.CorrespondenceList })),
)
const CorrespondenceNew = lazy(() =>
  import('../features/correspondence/New').then((m) => ({ default: m.CorrespondenceNew })),
)
const CorrespondenceDetail = lazy(() =>
  import('../features/correspondence/Detail').then((m) => ({ default: m.CorrespondenceDetail })),
)
const SubmittalList = lazy(() =>
  import('../features/submittals/List').then((m) => ({ default: m.SubmittalList })),
)
const SubmittalRegister = lazy(() =>
  import('../features/submittals/Register').then((m) => ({ default: m.SubmittalRegister })),
)
const SubmittalRoutes = lazy(() =>
  import('../features/submittals/Routes').then((m) => ({ default: m.SubmittalRoutes })),
)
const SubmittalNew = lazy(() =>
  import('../features/submittals/New').then((m) => ({ default: m.SubmittalNew })),
)
const SubmittalDetail = lazy(() =>
  import('../features/submittals/Detail').then((m) => ({ default: m.SubmittalDetail })),
)
const SubmittalAttachmentCanvas = lazy(() =>
  import('../features/submittals/AttachmentCanvas').then((m) => ({
    default: m.SubmittalAttachmentCanvas,
  })),
)
const MeetingsBoard = lazy(() =>
  import('../features/meetings/Board').then((m) => ({ default: m.MeetingsBoard })),
)
const MeetingNew = lazy(() =>
  import('../features/meetings/New').then((m) => ({ default: m.MeetingNew })),
)
const MeetingDetail = lazy(() =>
  import('../features/meetings/Detail').then((m) => ({ default: m.MeetingDetail })),
)
const MeetingSeriesScreen = lazy(() =>
  import('../features/meetings/Series').then((m) => ({ default: m.MeetingSeriesScreen })),
)
const DocumentSearch = lazy(() =>
  import('../features/documents/Search').then((m) => ({ default: m.DocumentSearch })),
)
const DrawingSet = lazy(() =>
  import('../features/drawings/Set').then((m) => ({ default: m.DrawingSet })),
)
const DrawingSheet = lazy(() =>
  import('../features/drawings/Sheet').then((m) => ({ default: m.DrawingSheet })),
)
const DrawingSets = lazy(() =>
  import('../features/drawings/Sets').then((m) => ({ default: m.DrawingSets })),
)
const DrawingSetReview = lazy(() =>
  import('../features/drawings/Review').then((m) => ({ default: m.DrawingSetReview })),
)
const ReviewSessions = lazy(() =>
  import('../features/drawings/Reviews').then((m) => ({ default: m.ReviewSessions })),
)
const ReviewSession = lazy(() =>
  import('../features/drawings/ReviewSession').then((m) => ({ default: m.ReviewSession })),
)
const DrawingSeriesList = lazy(() =>
  import('../features/drawings/Issuances').then((m) => ({ default: m.DrawingSeriesList })),
)
const DrawingSeries = lazy(() =>
  import('../features/drawings/Issuances').then((m) => ({ default: m.DrawingSeries })),
)
const DrawingIssuance = lazy(() =>
  import('../features/drawings/Issuances').then((m) => ({ default: m.DrawingIssuance })),
)
const SpecBook = lazy(() =>
  import('../features/specifications/Book').then((m) => ({ default: m.SpecBook })),
)
const SpecSection = lazy(() =>
  import('../features/specifications/Section').then((m) => ({ default: m.SpecSection })),
)
const SpecBooks = lazy(() =>
  import('../features/specifications/Books').then((m) => ({ default: m.SpecBooks })),
)
const SpecBookReview = lazy(() =>
  import('../features/specifications/Review').then((m) => ({ default: m.SpecBookReview })),
)
const MarkupScreen = lazy(() =>
  import('../features/markup/Markup').then((m) => ({ default: m.MarkupScreen })),
)
const ModulePage = lazy(() =>
  import('../features/generic/ModulePage').then((m) => ({ default: m.ModulePage })),
)
const DevicesScreen = lazy(() =>
  import('../features/directory/Devices').then((m) => ({ default: m.DevicesScreen })),
)
const PeopleScreen = lazy(() =>
  import('../features/directory/People').then((m) => ({ default: m.PeopleScreen })),
)
const AccessScreen = lazy(() =>
  import('../features/directory/Access').then((m) => ({ default: m.AccessScreen })),
)
const AccessRequestsScreen = lazy(() =>
  import('../features/directory/AccessRequests').then((m) => ({
    default: m.AccessRequestsScreen,
  })),
)
const ProjectMailScreen = lazy(() =>
  import('../features/directory/NotificationMail').then((m) => ({ default: m.ProjectMailScreen })),
)
const MyMailScreen = lazy(() =>
  import('../features/directory/NotificationMail').then((m) => ({ default: m.MyMailScreen })),
)
const WorkforceOverview = lazy(() =>
  import('../features/workforce/Overview').then((m) => ({ default: m.WorkforceOverview })),
)
const WorkforceTrends = lazy(() =>
  import('../features/workforce/Trends').then((m) => ({ default: m.WorkforceTrends })),
)
const WorkforcePlan = lazy(() =>
  import('../features/workforce/Plan').then((m) => ({ default: m.WorkforcePlan })),
)
const WorkforceActivities = lazy(() =>
  import('../features/workforce/Activities').then((m) => ({ default: m.WorkforceActivities })),
)
const WorkforceIndex = lazy(() =>
  import('../features/workforce/Index').then((m) => ({ default: m.WorkforceIndex })),
)
const EquipmentRegister = lazy(() =>
  import('../features/equipment/Register').then((m) => ({ default: m.EquipmentRegister })),
)
const EquipmentManifest = lazy(() =>
  import('../features/equipment/Manifest').then((m) => ({ default: m.EquipmentManifest })),
)
const EquipmentDetail = lazy(() =>
  import('../features/equipment/Detail').then((m) => ({ default: m.EquipmentDetail })),
)
const ModelsScreen = lazy(() =>
  import('../features/models/Models').then((m) => ({ default: m.ModelsScreen })),
)
const ScopeScreen = lazy(() =>
  import('../features/scope/Scope').then((m) => ({ default: m.ScopeScreen })),
)
const ModelViewer = lazy(() =>
  import('../features/models/Viewer').then((m) => ({ default: m.ModelViewer })),
)
const RepositoryScreen = lazy(() =>
  import('../features/repository/Repository').then((m) => ({ default: m.RepositoryScreen })),
)
const RepositoryFolder = lazy(() =>
  import('../features/repository/Repository').then((m) => ({ default: m.RepositoryFolder })),
)
const RepositoryDocument = lazy(() =>
  import('../features/repository/Repository').then((m) => ({ default: m.RepositoryDocument })),
)
const QaqcLotoPanel = lazy(() =>
  import('../features/qaqcloto/Panel').then((m) => ({ default: m.QaqcLotoPanel })),
)
const QaqcLotoLink = lazy(() =>
  import('../features/qaqcloto/Link').then((m) => ({ default: m.QaqcLotoLink })),
)

const queries = new QueryClient({
  defaultOptions: {
    queries: {
      // A site uplink drops. Retrying twice is worth it; retrying forever
      // turns a 403 into a spinner that never resolves.
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
})

/**
 * The session, and the saves held for the screens outside a job -- the site's
 * points -- above every route, so leaving a screen does not lose one. A job's
 * shell holds its own, keyed to the job.
 */
function SiteSession({ children }: { children: ReactNode }) {
  return (
    <SessionProvider>
      <UnsettledWorkProvider>{children}</UnsettledWorkProvider>
    </SessionProvider>
  )
}

export function App() {
  return (
    <QueryClientProvider client={queries}>
      <BrowserRouter>
        <SiteSession>
          <LazyScreens>
            <Routes>
              {/* The public front page, outside the gate because the person
                  reading it has no account yet. The addresses of the looks
                  that were offered before one was chosen lead back to it. */}
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/welcome/*" element={<Navigate to="/welcome" replace />} />
              <Route path="/signin" element={<SignIn />} />
              {/* Outside the gate for the same reason, and one step further:
                  the person here cannot sign in at all, which is what the code
                  in their hand is for. */}
              <Route path="/signin/reset" element={<ResetPassword />} />
              {/* And one step before that: the person here cannot sign in and
                  has no code either, so this is where they ask for one. */}
              <Route path="/signin/forgot" element={<ForgotPassword />} />
              {/* Outside the session gate, deliberately and for the same reason
                  `/signin` is: this is the request that makes a credential, so
                  a gate in front of it would redirect the tablet to a screen it
                  cannot use. */}
              <Route path="/device" element={<DeviceSignIn />} />
              {/* Outside the gate because nobody signs in on a television. The
                  page carries its own key in the address and sends it to one
                  route only; it never reads or sends a person's sign-in. */}
              <Route path="/display/board" element={<WallScreen />} />
              {/* Outside the gate, though it needs a person: the gate would send
                  somebody to sign in and forget the link they came with, so the
                  page asks for the sign-in itself and comes back holding it. */}
              <Route path="/invitation" element={<AcceptInvitation />} />
              <Route
                path="/"
                element={
                  <RequireSession>
                    <Projects />
                  </RequireSession>
                }
              />
              {/* Who runs the site, and where the Global Admin or a Super
                  Admin lands after signing in. Not one job's, so
                  outside the shell like the rest of administration. */}
              <Route
                path="/admin"
                element={
                  <RequireSession>
                    <AdminFrame>
                      <AdministrationScreen />
                    </AdminFrame>
                  </RequireSession>
                }
              />
              {/* Administration is not one job's, so it is not inside the
                  shell: the shell is a frame around a project and this screen
                  answers "what can this person do anywhere". It carries its own
                  way back into the job and its own sign-out for that reason. */}
              <Route
                path="/admin/access"
                element={
                  <RequireSession>
                    <AdminFrame>
                      <AccessScreen />
                    </AdminFrame>
                  </RequireSession>
                }
              />
              {/* Templates are the site's, not one job's: the frame they sit in
                  is administration's, for the reason the access screen gives. */}
              <Route
                path="/admin/templates"
                element={
                  <RequireSession>
                    <AdminFrame>
                      <TemplatesScreen />
                    </AdminFrame>
                  </RequireSession>
                }
              />
              <Route
                path="/admin/templates/:templateId"
                element={
                  <RequireSession>
                    <AdminFrame>
                      <TemplatesScreen />
                    </AdminFrame>
                  </RequireSession>
                }
              />
              {/* The queue is not one job's, so it is not inside the shell:
                  a request names no project, deliberately, and there is no
                  project for a frame around it to be about. It carries its own
                  sign-out for that reason; the administration bar has the way
                  back. */}
              <Route
                path="/admin/access-requests"
                element={
                  <RequireSession>
                    <AdminFrame>
                      <AccessRequestsScreen />
                    </AdminFrame>
                  </RequireSession>
                }
              />
              {/* Outside the shell on purpose: the shell is a job's, and the
                  person this screen exists for may be on no job at all. */}
              <Route
                path="/access"
                element={
                  <RequireSession>
                    <AskForAccessScreen />
                  </RequireSession>
                }
              />
              {/* Outside the shell: what this administers is the address's,
                  not one job's, so there is no job to put a shell around. */}
              <Route
                path="/admin/projects"
                element={
                  <RequireSession>
                    <AdminFrame>
                      <ProjectSetupScreen />
                    </AdminFrame>
                  </RequireSession>
                }
              />
              {/* The instance's, not a job's or an address's: the keys this
                  site calls other services with. The Global Admin's alone, which
                  the server enforces; the rail only offers it to them. */}
              <Route
                path="/admin/integrations"
                element={
                  <RequireSession>
                    <AdminFrame>
                      <IntegrationsScreen />
                    </AdminFrame>
                  </RequireSession>
                }
              />
              {/* The site's prices for each action, the same on every job.
                  A super admin's or the global admin's, which the server
                  enforces; the rail only offers it to them. */}
              <Route
                path="/admin/points"
                element={
                  <RequireSession>
                    <AdminFrame>
                      <PointsScreen />
                    </AdminFrame>
                  </RequireSession>
                }
              />
              {/* The instance's too: how somebody added to a job gets in. The
                  Global Admin's alone, like Integrations. */}
              <Route
                path="/admin/signin"
                element={
                  <RequireSession>
                    <AdminFrame>
                      <SignInPolicyScreen />
                    </AdminFrame>
                  </RequireSession>
                }
              />
              {/* What every project's wall shows under the names. The Super
                  Admins' and the Global Admin's, which the server enforces;
                  the rail only offers it to them. */}
              <Route
                path="/admin/wall-screens"
                element={
                  <RequireSession>
                    <AdminFrame>
                      <WallScreensScreen />
                    </AdminFrame>
                  </RequireSession>
                }
              />
              <Route
                path="/admin/site-computers"
                element={
                  <RequireSession>
                    <AdminFrame>
                      <SiteComputersScreen />
                    </AdminFrame>
                  </RequireSession>
                }
              />
              {/* Outside the shell: a television on a trailer wall shows the
                  board and nothing to click. */}
              <Route
                path="/p/:projectId/board/display"
                element={
                  <RequireSession>
                    <BoardDisplay />
                  </RequireSession>
                }
              />
              <Route
                element={
                  <RequireSession>
                    <Shell />
                  </RequireSession>
                }
              >
                <Route path="/p/:projectId" element={<Overview />} />
                <Route path="/p/:projectId/qaqc-loto" element={<QaqcLotoPanel />} />
                <Route path="/p/:projectId/qaqc-loto/link" element={<QaqcLotoLink />} />
                <Route path="/p/:projectId/deficiency" element={<DeficiencyList />} />
                <Route path="/p/:projectId/deficiency/new" element={<DeficiencyNew />} />
                <Route path="/p/:projectId/deficiency/:recordId" element={<DeficiencyDetail />} />
                <Route path="/p/:projectId/schedule" element={<ScheduleScreen />} />
                <Route path="/p/:projectId/schedule/lookahead" element={<LookaheadScreen />} />
                <Route
                  path="/p/:projectId/schedule/activities/:refId"
                  element={<ScheduleActivity />}
                />
                <Route path="/p/:projectId/dailylog" element={<DailyLogList />} />
                <Route path="/p/:projectId/dailylog/:dayId" element={<DailyLogDay />} />
                <Route path="/p/:projectId/fieldreport" element={<FieldReportList />} />
                <Route path="/p/:projectId/fieldreport/:reportId" element={<FieldReportScreen />} />
                <Route path="/p/:projectId/photos" element={<PhotosScreen />} />
                {/* System status, from the button on the bar. Inside the job so
                    the frame stays around it; what it draws is the same on
                    every job, because there is one system per site. */}
                <Route path="/p/:projectId/system" element={<SystemStatusScreen />} />
                {/* Reports filed with the button on every screen, found again:
                    the reporter's own, the board, and one report. Reached from
                    the button's own window; the menu offers them only by the one choice
                    in nav.ts, FEEDBACK_IN_MENU. */}
                <Route path="/p/:projectId/feedback" element={<FeedbackMine />} />
                <Route path="/p/:projectId/feedback/triage" element={<FeedbackBoard />} />
                <Route path="/p/:projectId/feedback/:reportId" element={<FeedbackReportScreen />} />
                {/* Directory and permissions: the project directory, People,
                    Access and the request queue as four sections of one area,
                    inside the job. Access and the queue still answer at their
                    addresses under /admin as well, in the administration frame,
                    so nothing anybody saved stops opening. */}
                <Route path="/p/:projectId/directory" element={<DirectoryArea />}>
                  <Route index element={<DirectoryScreen />} />
                  <Route path="people" element={<PeopleScreen />} />
                  <Route path="access" element={<AccessScreen inJob />} />
                  <Route path="access-requests" element={<AccessRequestsScreen inJob />} />
                  <Route path="notification-mail" element={<ProjectMailScreen />} />
                </Route>
                <Route path="/p/:projectId/changes" element={<ChangeRegister />} />
                <Route path="/p/:projectId/changes/new" element={<ChangeEventNew />} />
                <Route path="/p/:projectId/changes/settings" element={<ChangeSettings />} />
                <Route path="/p/:projectId/changes/:eventId" element={<ChangeEventDetail />} />
                <Route path="/p/:projectId/rfi" element={<RfiList />} />
                <Route path="/p/:projectId/rfi/new" element={<RfiNew />} />
                <Route path="/p/:projectId/rfi/routes" element={<RfiRoutes />} />
                <Route path="/p/:projectId/rfi/:recordId" element={<RfiDetail />} />
                {/* Above the record route: a static segment outranks a
                    dynamic one, so "register" and "new" are screens rather than
                    submittals whose id happens to spell them. Same reason the
                    meetings series route sits where it does. */}
                <Route path="/p/:projectId/correspondence" element={<CorrespondenceList />} />
                <Route path="/p/:projectId/correspondence/new" element={<CorrespondenceNew />} />
                <Route
                  path="/p/:projectId/correspondence/:recordId"
                  element={<CorrespondenceDetail />}
                />
                <Route path="/p/:projectId/submittals" element={<SubmittalList />} />
                <Route path="/p/:projectId/submittals/register" element={<SubmittalRegister />} />
                <Route path="/p/:projectId/submittals/routes" element={<SubmittalRoutes />} />
                <Route path="/p/:projectId/submittals/new" element={<SubmittalNew />} />
                <Route path="/p/:projectId/submittals/:recordId" element={<SubmittalDetail />} />
                <Route
                  path="/p/:projectId/submittals/:recordId/files/:attachmentId"
                  element={<SubmittalAttachmentCanvas />}
                />
                <Route path="/p/:projectId/meetings" element={<MeetingsBoard />} />
                <Route path="/p/:projectId/meetings/new" element={<MeetingNew />} />
                {/* Above the record route deliberately: a static segment
                    outranks a dynamic one in the router, so "series" is a
                    screen rather than a meeting whose id happens to spell it. */}
                <Route path="/p/:projectId/meetings/series" element={<MeetingSeriesScreen />} />
                <Route path="/p/:projectId/meetings/:recordId" element={<MeetingDetail />} />
                <Route path="/p/:projectId/devices" element={<DevicesScreen />} />
                <Route path="/p/:projectId/people" element={<MovedIntoDirectory slug="people" />} />
                <Route path="/p/:projectId/my-email" element={<MyMailScreen />} />
                <Route path="/p/:projectId/documents" element={<DocumentSearch />} />
                <Route path="/p/:projectId/drawings" element={<DrawingSet />} />
                {/* Ahead of the sheet route, for the reason the meeting series
                    is ahead of the meeting: otherwise `/drawings/sets` opens as
                    a sheet whose id happens to spell "sets". */}
                <Route path="/p/:projectId/drawings/sets" element={<DrawingSets />} />
                <Route path="/p/:projectId/drawings/sets/:setId" element={<DrawingSetReview />} />
                <Route path="/p/:projectId/drawings/reviews" element={<ReviewSessions />} />
                <Route
                  path="/p/:projectId/drawings/reviews/:sessionId"
                  element={<ReviewSession />}
                />
                <Route path="/p/:projectId/drawings/series" element={<DrawingSeriesList />} />
                <Route path="/p/:projectId/drawings/series/:seriesId" element={<DrawingSeries />} />
                <Route
                  path="/p/:projectId/drawings/series/:seriesId/issuances/:setId"
                  element={<DrawingIssuance />}
                />
                <Route path="/p/:projectId/drawings/:sheetId" element={<DrawingSheet />} />
                <Route path="/p/:projectId/specs" element={<SpecBook />} />
                {/* Ahead of the section route, for the same reason the set
                    list is ahead of the sheet. */}
                <Route path="/p/:projectId/specs/books" element={<SpecBooks />} />
                <Route path="/p/:projectId/specs/books/:bookId" element={<SpecBookReview />} />
                <Route path="/p/:projectId/specs/:sectionId" element={<SpecSection />} />
                <Route path="/p/:projectId/markup" element={<MarkupScreen />} />
                <Route path="/p/:projectId/models" element={<ModelsScreen />} />
                <Route path="/p/:projectId/models/:versionId" element={<ModelViewer />} />
                <Route path="/p/:projectId/scope" element={<ScopeScreen />} />
                <Route path="/p/:projectId/repository" element={<RepositoryScreen />} />
                <Route
                  path="/p/:projectId/repository/folders/:folderId"
                  element={<RepositoryFolder />}
                />
                <Route
                  path="/p/:projectId/repository/documents/:documentId"
                  element={<RepositoryDocument />}
                />
                <Route path="/p/:projectId/workforce" element={<WorkforceOverview />} />
                <Route path="/p/:projectId/workforce/trends" element={<WorkforceTrends />} />
                <Route
                  path="/p/:projectId/workforce/planned-vs-present"
                  element={<WorkforcePlan />}
                />
                <Route
                  path="/p/:projectId/workforce/by-activity"
                  element={<WorkforceActivities />}
                />
                <Route path="/p/:projectId/workforce/index" element={<WorkforceIndex />} />
                <Route path="/p/:projectId/equipment" element={<EquipmentRegister />} />
                <Route path="/p/:projectId/equipment/manifest" element={<EquipmentManifest />} />
                <Route path="/p/:projectId/equipment/:recordId" element={<EquipmentDetail />} />
                <Route path="/p/:projectId/m/:moduleCode" element={<ModulePage />} />
                {/* Inside the shell deliberately, and scoped to a project just
                    as deliberately. An unknown path under a project used to
                    match nothing here and fall through to the redirect below,
                    which discards the project id: the job closes and the
                    reader lands on the chooser with nothing saying the address
                    was what was wrong. (Before #254 it went further still, to
                    the first job the person held, so the reader was put on
                    another job's overview.) Matched here, the project stays
                    open and the page says the address is unknown.
                    The project has to be named in the path. A bare `*` here
                    repairs nothing, and it was tried: this route sits in a
                    layout with no path of its own, so the splat matches from
                    the root, `useParams` gives the shell no `projectId`, and
                    the shell's own guard sends the reader to the chooser —
                    which is the fault this exists to fix, reached by a second
                    route. */}
                <Route path="/p/:projectId/*" element={<UnknownAddress />} />
              </Route>
              {/* Still the answer for an address with no project in it at all,
                  which is the case it was written for. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </LazyScreens>
        </SiteSession>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
